import {
  Component,
  OnChanges,
  ChangeDetectorRef,
  ViewChildren,
  QueryList,
  Output,
  EventEmitter,
  ViewChild,
  OnInit,
  OnDestroy, Input
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, UntypedFormArray } from '@angular/forms';
import * as _ from 'lodash';

import { TableComponent } from 'shared/table/table/table.component';

import { createFormControl } from 'utils/form-control';

import { fieldsInfo } from './fields-info';
import {ProductPanelComponent} from '../../../../product-panel/product-panel.component';
import { rowData, contentData, collapseWidthPx, subGroupData } from './table-data';
import {DomSanitizer} from '@angular/platform-browser';
import {CollapseGroupComponent} from 'shared/collapse/collapse-group/collapse-group.component';
import {LoaderComponent} from 'shared/loader/loader.component';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import { TableEditableComponent } from 'shared/table/table-editable/table-editable.component';


@Component({
  moduleId: module.id,
  selector: 'product-groups-form',
  templateUrl: 'product-groups-form.component.html',
  styleUrls: ['product-groups-form.component.scss'],
})

export class ProductGroupsFormComponent extends TableComponent implements OnChanges, OnInit, OnDestroy {
  @Input() savingEvents: Observable<void>;

  headers: any[] = ['name', 'sub_groups', 'code', 'item_count'];
  fieldsInfo: any = fieldsInfo;
  form: UntypedFormGroup;
  declare validators: any;

  @ViewChild(TableEditableComponent, {static: false}) tableEditable: TableEditableComponent | void;
  @ViewChildren(CollapseGroupComponent) collapseGroups: QueryList<CollapseGroupComponent>;
  @ViewChild(LoaderComponent, {static: true})
  loader: LoaderComponent;

  rowData: any[] = rowData;
  contentData: any[] = contentData;
  subGroupData: any[] = subGroupData;
  collapseWidth = collapseWidthPx;
  controlClassPrefix: string = 'product-groups-control-row-';
  editMode: boolean = false;

  @Output() cancel: EventEmitter<any> = new EventEmitter();
  @Output() saveChanges: EventEmitter<any> = new EventEmitter();

  private savingEventsSubscription: Subscription;

  // public:
  get productGroupsFormData(): UntypedFormArray {
    return this.form.get('productGroupsFormData') as UntypedFormArray;
  }

  get isInValid(): boolean {
    return this.form.invalid;
  }

  get isEmpty(): boolean {
    return !_.get(this.productGroupsFormData, 'controls.length');
  }

  get formArray(): UntypedFormArray {
    return this.productGroupsFormData;
  }

  get isEditMode(): boolean {
    return this.editMode;
  }

  set isEditMode(value: boolean) {
    this.editMode = value;
  }

  toggleEditMode() {
    this.editMode = !this.editMode;
  }

  get isFormChanged(): boolean {
    if (this.isEditMode) {
      return !!this.form.dirty;
    } else {
      return false;
    }
  }

  constructor(
    private fb: UntypedFormBuilder,
    private ref: ChangeDetectorRef,
    protected sanitizer: DomSanitizer,
  ) {
    super();
    this.createForm();
  }

  ngOnInit() {
    this.savingEventsSubscription = this.savingEvents.subscribe(() => {
      this.editMode = false;
    });
  }

  ngOnChanges(changes: any) {
    this.setValidators();
    this.setFormData();
  }

  ngOnDestroy() {
    this.savingEventsSubscription.unsubscribe();
  }

  createForm() {
    this.form = this.fb.group({
      productGroupsFormData: this.fb.array([]),
    });
  }

  setFormData() {
    if (!this.data) { return; }
    const productGroupFGs = this.data.map((item: any) => {
      return this.createFormGroupItem(item);
    });
    const formArray = this.fb.array(productGroupFGs);
    this.form.setControl('productGroupsFormData', formArray);
  }

  getFieldWidthClass(fieldName: string) {
    // width class for cells
    return `size-${this.getFieldCellSize(fieldName, this.fieldsInfo)}`;
  }

  getFieldOptions(name: string): Array<any> {
    return this.getOptions(name, this.segmentData);
  }

  getError() {
    let error = '';
    // search for empty code in groups
    let itemWithoutCode = _.find(this.productGroupsFormData.value, {code: ''});
    if (itemWithoutCode) { return 'All Groups and Sub Groups must have a code'; }

    // search for empty code in subgroups
    _.forEach(this.productGroupsFormData.value, (productGroup: any) => {
      itemWithoutCode = _.find(productGroup.productSubGroup, {code: ''});
      if (itemWithoutCode) {
        error = 'All Groups and Sub Groups must have a code';
        return;
      }
    });
    return error;
  }

  // handlers:
  onAddSubGroup(productSubGroup: UntypedFormArray) {
    const controls = this.createSubGroupControls();
    productSubGroup.push(this.fb.group(controls));
    this.form.markAsDirty();
  }

  onDeleteSubGroupItem(productSubGroup: UntypedFormArray, index: number) {
    productSubGroup.removeAt(index);
    this.form.markAsDirty();
    productSubGroup.updateValueAndValidity();
  }

  onAddNewRowClick() {
    const newGroup = this.createFormGroupItem();
    this.productGroupsFormData.push(newGroup);
    this.form.markAsDirty();
  }

  // define ghost attribute labels:

  getGroupSelector(formGroupData: any, index: number): string {
    if (_.has(formGroupData, 'code') && formGroupData.code.value.length > 0) {
      return 'group-row-' + formGroupData.code.value;
    } else if (formGroupData.id) {
      return 'group-row-' + formGroupData.id.value;
    } else {
      return 'group-row-new';
    }
  }

  getInputSelector(formGroupData: any, field: string): string {
    if (formGroupData.product_group_id) {
      return 'input-sub-' + field;
    } else {
      return 'input-' + field;
    }
  }

  public fieldValidation(item: any, formGroup: UntypedFormGroup): any {
    if (!item) {
      return;
    }
    let validation = super.fieldValidation(item, formGroup);
    // PIES 6.7 validation row was added for test
    const pies65: any = _.find(validation, {name: 'PIES_6_5'});
    if (pies65) {
      validation = [...validation, {
        key: 'pies67',
        title: 'PIES 6.7',
        valid: pies65.valid,
      }];
    }
    return validation;
  }

  public getField(fieldMap: string): any {
    // @ts-ignore
    return _.find(this.segmentData, {field_map: fieldMap});
  }

  protected getTitle(name: string): string {
    // find title in rowData
    // @ts-ignore
    const colData = _.find(this.rowData, { fieldName: name });
    if (_.get(colData, 'title')) {
      return colData.title;
    }

    // find title in segmentData
    // @ts-ignore
    const item = _.find(this.segmentData, { field_map: name });
    return item ? item.title : '';
  }

  protected getInvalidDot(fieldMap: string, formGroup: UntypedFormGroup, index: number) {
    const control = _.get(formGroup, `controls.${fieldMap}`);
    if (!control) {
      return null;
    }
    if (control.invalid) {
      return   this.sanitizer.bypassSecurityTrustHtml(`
        <div class="circle-icon size-small cell-invalid-dot circle-red"
        data-ghost="cell-invalid-dot-${fieldMap}-${index}"></div>
      `);
    } else {
      return null;
    }
  }

  protected collapseRow(index: number, collapse?: boolean) {
    // pass -1 to collapse all
    const collapseGroupsArr: any = this.collapseGroups.toArray();

    if (!_.get(collapseGroupsArr, 'length') || !_.get(collapseGroupsArr, `[${index}]`)) { return; }
    if (_.isNil(collapse)) { collapse = !collapseGroupsArr[index].collapsed; }
    collapseGroupsArr[index].setCollapsed(collapse);

    this.ref.markForCheck();
  }

  protected getPlaceholder(fieldMap: string) {
    return (fieldMap === 'item_count') ? '0' : '-';
  }

  // private:

  private createFormGroupItem(item: any = {
    code: '',
    name: '',
    description: '',
    item_count: '',
    product_sub_group: null
  }): UntypedFormGroup {
    const controls = this.createGroupControls(item);
    const fbGroup: UntypedFormGroup = this.fb.group(controls);
    const subGroupArray: Array<any> = this.createSubGroupArray(item.product_sub_group);
    fbGroup.setControl(
      'productSubGroup',
      this.fb.array(subGroupArray, this.subGroupCodeValidators.bind(this))
    );
    return fbGroup;
  }

  private createGroupControls(obj: any = {
    code: '',
    name: '',
    description: '',
    item_count: '',
    product_sub_group: null
  }): any {
    return _.reduce((obj), (result: any, value: any, key: string) => {
      if (key === 'product_sub_group') { return result; }
      result[key] = createFormControl(key, value, this.validators);
      return result;
    }, {});
  }

  private createSubGroupArray(subGroups: Array<any>): Array<any> {
    if (!subGroups) { return []; }
    return _.reduce(subGroups, (result: Array<UntypedFormGroup>, item: any) => {
      const formGroupControls = this.createSubGroupControls(item);
      result.push(this.fb.group(formGroupControls));
      return result;
    }, []);
  }

  private createSubGroupControls(obj: any = {
    id: null,
    code: '',
    name: '',
    description: '',
    item_count: 0
  }): any {
    return _.reduce(obj, (result: any, value: any, key: string) => {
      // change name for code for getting sub group code validation
      const name = key === 'code' ? 'sub_group_code' : key;
      result[key] = createFormControl(name, value, this.validators);
      return result;
    }, {});
  }

  private subGroupCodeValidators(formArray: UntypedFormArray): {[key: string]: any} {
    const identicalControls = this.getIdenticalControls(formArray);
    let hasIdentical = null;
    _.each(_.values(identicalControls), (groups: Array<UntypedFormGroup>) => {
      if (groups.length < 2) {
        const control = groups[0].get('code');
        if (!control.hasError('uniq')) { return; }
        this.setUniqueError(control, null);
      } else {
        hasIdentical = true;
        _.each(groups, (group: UntypedFormGroup) => {
          const control = group.get('code');
          if (control.hasError('uniq')) { return; }
          this.setUniqueError(control, {'uniq': 'Must be unique'});
        });
      }
    });
    return hasIdentical;
  }

  private setUniqueError(control: AbstractControl, error: any) {
    _.defer(() => control.setErrors(error));
  }

  private getIdenticalControls(formArray: UntypedFormArray): any {
    return _.groupBy(formArray.controls, (group: UntypedFormGroup) => {
      return group.get('code').value;
    });
  }

  getStaticValue(fieldName, formGroup): string {
    return fieldName === 'subGroups' ? formGroup.controls.productSubGroup.length : null;
  }

  onRowRemove(formGroup: UntypedFormGroup) {
    const index = this.formArray.controls.indexOf(formGroup);
    this.formArray.removeAt(index);
    this.data.splice(index, 1);
    this.formArray.markAsDirty();
  }

  onCollapseClick(index: number) {
    this.collapseRow(index);
  }

  onViewEditSaveClick() {
    if (this.isFormChanged) {
      this.saveChanges.emit();
    } else {
      this.toggleEditMode();
    }
  }

  onCancelClick() {
    this.cancel.emit();
  }

  onRowAdd() {
    this.onAddNewRowClick();
  }

  onRemoveSubGroupClick(item: UntypedFormGroup, index: number) {
    const subGroupsArray = item.controls.productSubGroup as UntypedFormArray;
    subGroupsArray.removeAt(index);
    this.form.markAsDirty();
    subGroupsArray.updateValueAndValidity();
  }
  onAddAnotherSubGroupClick(item: UntypedFormGroup) {
    const subGroupsArray = item.controls.productSubGroup as UntypedFormArray;
    this.onAddSubGroup(subGroupsArray);
  }
}
